import axiosInstance from './axios';

export interface AuditYear {
  id: string;
  organization_id: string;
  year: number;
  name: string;
  start_date?: string;
  end_date?: string;
  is_active: boolean;
  is_locked: boolean;
  locked_at?: string;
  created_at: string;
  updated_at: string;
  has_data?: boolean;
}

export interface CreateAuditYearPayload {
  year: number;
  name: string;
  start_date?: string;
  end_date?: string;
}

export interface UpdateAuditYearPayload {
  name?: string;
  is_active?: boolean;
}

export const listAuditYears = async (): Promise<AuditYear[]> => {
  const res = await axiosInstance.get('/api/audit-years');
  return res.data.audit_years ?? [];
};

export const createAuditYear = async (payload: CreateAuditYearPayload): Promise<AuditYear> => {
  const res = await axiosInstance.post('/api/audit-years', payload);
  return res.data.audit_year;
};

export const getAuditYear = async (id: string): Promise<AuditYear> => {
  const res = await axiosInstance.get(`/api/audit-years/${id}`);
  return res.data.audit_year;
};

export const updateAuditYear = async (id: string, payload: UpdateAuditYearPayload): Promise<AuditYear> => {
  const res = await axiosInstance.patch(`/api/audit-years/${id}`, payload);
  return res.data.audit_year;
};

export const lockAuditYear = async (id: string): Promise<void> => {
  await axiosInstance.post(`/api/audit-years/${id}/lock`);
};

export const deleteAuditYear = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/api/audit-years/${id}`);
};

// ─── Lock Readiness ───────────────────────────────────────────────────────────

export interface LockReadiness {
  can_lock: boolean;
  /** Hard blockers — must all be resolved before lock is permitted. */
  blockers: string[];
  /** Soft warnings — informational, lock still allowed. */
  warnings: string[];
}

/** Read-only: runs pre-lock checks and returns blockers + warnings. No data is written. */
export const getLockReadiness = async (id: string): Promise<LockReadiness> => {
  const res = await axiosInstance.get(`/api/audit-years/${id}/lock-readiness`);
  return res.data.readiness;
};

// ─── Backfill ─────────────────────────────────────────────────────────────────

export interface BackfillCounts {
  processes: number;
  sox_issues: number;
  test_runs: number;
  documents: number;
  test_executions: number;
  process_risk_controls: number;
  pbc_requests: number;
  materiality_assessments: number;
  system_itgc_controls: number;
  total: number;
}

/** Read-only: returns per-table counts of rows with no audit year assigned. */
export const previewBackfill = async (id: string): Promise<BackfillCounts> => {
  const res = await axiosInstance.get(`/api/audit-years/${id}/backfill-preview`);
  return res.data.counts;
};

/** Writes: assigns all unscoped (audit_year_id = NULL) rows to this audit year. */
export const backfillAuditYear = async (id: string): Promise<{ counts: BackfillCounts; message: string }> => {
  const res = await axiosInstance.post(`/api/audit-years/${id}/backfill`);
  return res.data;
};
